import { useAtomValue, useSetAtom } from "jotai";
import { PreloadedQuery } from "react-relay";

import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { useGetBundledRateOptionsQuery as UseGetBundledRateOptionsQueryType } from "../../../../../../../../../hooks/__generated__/useGetBundledRateOptionsQuery.graphql";
import {
  useBundledRateOptionsQuery,
  useGetBundledRateOptions,
} from "../../../../../../../../../hooks/useGetBundledRateOptions";
import { useGetSingleBundleRateOption } from "../../../../../../../../../hooks/useGetSingleBundleRateOption";
import { setRateIdOnRoundAtom } from "../../../../../../atoms/actions/setRateIdOnRoundAtom/setRateIdOnRoundAtom";
import { setTransportationIdTypeOnRoundAtom } from "../../../../../../atoms/actions/setTransportationTypeIdOnRoundAtom/setTransportationTypeIdOnRoundAtom";
import { getCourseLayoutHoleCountOnRoundAtom } from "../../../../../../atoms/selectors/getCourseLayoutHoleCountOnRoundAtom/getCourseLayoutHoleCountOnRoundAtom";
import { getRateOnRoundAtom } from "../../../../../../atoms/selectors/getRateOnRoundAtom/getRateOnRoundAtom";
import { getTransportationTypeIdOnRoundAtom } from "../../../../../../atoms/selectors/getTransportationTypeIdOnRoundAtom/getTransportationTypeIdOnRoundAtom";
import { selectedRoundAtom } from "../../../../../../atoms/stores";

type Props = {
  membershipTypeId: string | null;
  currentClientId: string | null;
  currentClientType: "MEMBER" | "CUSTOMER";
  timeSlotId: string | null | undefined;
};

export const ConfigureRoundBundledRateAndTransportSelect = ({
  membershipTypeId,
  currentClientId,
  currentClientType,
  timeSlotId,
}: Props) => {
  const currentHoleCount = useAtomValue(getCourseLayoutHoleCountOnRoundAtom);
  const selectedTransportationTypeId = useAtomValue(
    getTransportationTypeIdOnRoundAtom
  );
  const currentRate = useAtomValue(getRateOnRoundAtom);
  /**
   * If the current rate is locked -- then we only show a single option.
   * This can happen if the staff sets a rate the user cannot normally see.
   */

  const selectedRound = useAtomValue(selectedRoundAtom);

  const isCurrentRateLocked = currentRate?.isLocked;
  const { queryRef } = useBundledRateOptionsQuery({
    timeSlotId,
    membershipTypeId,
    visibleToMembershipTypeId: membershipTypeId,
    rateType: currentClientType,
    nonPlayingHost: false,
    transportationMemberId: currentClientId,
    transportationPlayerType: currentClientType,
  });

  const currentCombinedId = `${currentRate?.id}.${selectedTransportationTypeId}`;

  if (!queryRef) {
    return null;
  }

  return (
    <ConfigureRoundBundledRateAndTransportSelectInner
      currentCombinedId={currentCombinedId}
      currentHoleCount={currentHoleCount}
      isCurrentRateLocked={!!isCurrentRateLocked}
      queryRef={queryRef}
      selectedRound={selectedRound}
    />
  );
};

type ConfigureRoundBundledRateAndTransportSelectInnerProps = {
  currentCombinedId: string;
  currentHoleCount: Parameters<typeof useGetSingleBundleRateOption>[1];
  isCurrentRateLocked: boolean;
  queryRef: PreloadedQuery<UseGetBundledRateOptionsQueryType>;
  selectedRound: Parameters<typeof useGetSingleBundleRateOption>[0];
};

const ConfigureRoundBundledRateAndTransportSelectInner = ({
  currentCombinedId,
  currentHoleCount,
  isCurrentRateLocked,
  queryRef,
  selectedRound,
}: ConfigureRoundBundledRateAndTransportSelectInnerProps) => {
  const setRateIdOnRound = useSetAtom(setRateIdOnRoundAtom);
  const setTransportationIdTypeOnRound = useSetAtom(
    setTransportationIdTypeOnRoundAtom
  );
  const selectedTransportationTypeId = useAtomValue(
    getTransportationTypeIdOnRoundAtom
  );
  const singleOption = useGetSingleBundleRateOption(
    selectedRound,
    currentHoleCount,
    queryRef,
    selectedTransportationTypeId
  );
  const bundledOptions = useGetBundledRateOptions(
    currentHoleCount,
    queryRef,
    selectedRound
  );
  const optionsToUse = isCurrentRateLocked ? [singleOption] : bundledOptions;

  return (
    <VStack flex={1}>
      <DropdownSelect
        hideOuterMargin
        hideChevron
        includeLeftElementInSelectedValue
        isViewOnly={isCurrentRateLocked}
        options={optionsToUse}
        value={currentCombinedId}
        onChange={(value) => {
          if (value) {
            const [rateId, ttId] = value.split(".");
            setRateIdOnRound({
              rateId,
            });
            setTransportationIdTypeOnRound({
              transportationTypeId: ttId,
            });
          }
        }}
        testID="Bundled Rate Dropdown Selector"
      />
    </VStack>
  );
};
